import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  /** @type {AbortController | null} */
  #quickViewModalAbortController = null;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleJd1QvVariantUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    this.#quickViewModalAbortController?.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    const slideshowSlides = dialogComponent.querySelector('.product-information__media slideshow-slides');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
    slideshowSlides?.scrollTo({ left: 0, top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Reads the media payload generated by snippets/quick-add.liquid.
   * Falls back to product-card images if the snippet has not been replaced yet.
   * @returns {Array<{src: string, src_mobile?: string, alt?: string, id?: string | number}>}
   */
  #getJd1QuickViewMediaData() {
    const raw = this.dataset.jd1QvMedia;

    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          const clean = parsed.filter((item) => item && typeof item.src === 'string' && item.src.length > 0);
          if (clean.length) return clean;
        }
      } catch (error) {
        // Keep the modal resilient if an old/browser-cached snippet outputs malformed data.
        console.warn('JD1 quick view media payload could not be parsed.', error);
      }
    }

    const productCard = /** @type {HTMLElement | null} */ (this.closest('product-card'));
    if (!productCard) return [];

    const seen = new Set();
    const fallbackImages = [];
    const images = productCard.querySelectorAll('img');

    for (const image of images) {
      const srcset = image.getAttribute('srcset') || image.dataset.srcset || '';
      const firstSrcsetCandidate = srcset.split(',')[0]?.trim().split(/\s+/)[0];
      const src =
        image.currentSrc ||
        image.src ||
        image.getAttribute('src') ||
        image.dataset.src ||
        image.dataset.lazySrc ||
        image.dataset.original ||
        firstSrcsetCandidate;

      const normalizedSrc = this.#normalizeJd1QuickViewMediaUrl(src);
      if (!normalizedSrc || seen.has(normalizedSrc)) continue;
      seen.add(normalizedSrc);
      fallbackImages.push({
        src: normalizedSrc,
        alt: image.alt || this.dataset.productTitle || '',
      });
    }

    return fallbackImages;
  }


  /**
   * Reads the variant/media payload generated by snippets/quick-add.liquid.
   * @returns {Array<{id: string | number, media_id?: string | number | null, price?: string, price_raw?: number, compare_at_price?: string | null, compare_at_price_raw?: number | null}>}
   */
  #getJd1QuickViewVariantMediaData() {
    const raw = this.dataset.jd1QvVariants;
    if (!raw) return [];

    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((item) => item && item.id);
    } catch (error) {
      console.warn('JD1 quick view variant payload could not be parsed.', error);
      return [];
    }
  }


  /**
   * Escapes strings before injecting payload-based price markup.
   * @param {string | number | null | undefined} value
   * @returns {string}
   */
  #escapeJd1QvHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /**
   * Gets variant pricing data from the Liquid payload.
   * @param {string | number | null | undefined} variantId
   * @returns {{id?: string | number, price?: string, price_raw?: number, compare_at_price?: string | null, compare_at_price_raw?: number | null} | null}
   */
  #getJd1QuickViewVariantPriceData(variantId) {
    const variants = this.#getJd1QuickViewVariantMediaData();
    const normalizedVariantId = variantId ? String(variantId) : '';
    const selectedVariant = normalizedVariantId
      ? variants.find((item) => String(item.id) === normalizedVariantId)
      : null;

    return selectedVariant || variants[0] || {
      id: normalizedVariantId || '',
      price: this.dataset.jd1QvPrice || '',
      price_raw: Number(this.dataset.jd1QvPriceRaw || 0),
      compare_at_price: this.dataset.jd1QvComparePrice || null,
      compare_at_price_raw: this.dataset.jd1QvComparePriceRaw ? Number(this.dataset.jd1QvComparePriceRaw) : null,
    };
  }

  /**
   * Builds the visible quick-view price markup that sits below swatches and above CTA.
   * @param {string | number | null | undefined} variantId
   * @returns {string}
   */
  #buildJd1QuickViewPriceMarkup(variantId) {
    const variant = this.#getJd1QuickViewVariantPriceData(variantId);
    const price = variant?.price || this.dataset.jd1QvPrice || '';
    const compareAt = variant?.compare_at_price || this.dataset.jd1QvComparePrice || '';
    const priceRaw = Number(variant?.price_raw || this.dataset.jd1QvPriceRaw || 0);
    const compareAtRaw = Number(variant?.compare_at_price_raw || this.dataset.jd1QvComparePriceRaw || 0);
    const hasCompareAt = Boolean(compareAt && compareAtRaw > priceRaw);

    if (!price) return '';

    return `
      <div class="jd1-qv-price__inner" data-jd1-qv-price-variant-id="${this.#escapeJd1QvHtml(variant?.id || variantId || '')}">
        ${hasCompareAt ? `<span class="jd1-qv-price__compare">${this.#escapeJd1QvHtml(compareAt)}</span>` : ''}
        <span class="jd1-qv-price__current${hasCompareAt ? ' jd1-qv-price__current--sale' : ''}">${this.#escapeJd1QvHtml(price)}</span>
      </div>
    `;
  }

  /**
   * Creates the JD1 quick-view price block from product/variant Liquid payload.
   * @param {string | number | null | undefined} variantId
   * @returns {HTMLElement | null}
   */
  #createJd1QuickViewPriceBlock(variantId) {
    const markup = this.#buildJd1QuickViewPriceMarkup(variantId);
    if (!markup.trim()) return null;

    const priceBlock = document.createElement('div');
    priceBlock.classList.add('jd1-qv-price', 'jd1-qv-price--payload');
    priceBlock.setAttribute('aria-live', 'polite');
    priceBlock.innerHTML = markup;
    return priceBlock;
  }

  /**
   * Updates the price under the swatches whenever the modal variant changes.
   * @param {string | number | null | undefined} variantId
   * @param {Element | null | undefined} modalContent
   */
  #updateJd1QuickViewPrice(variantId, modalContent) {
    if (!modalContent) return;
    const priceBlock = modalContent.querySelector('.jd1-qv-price--payload');
    if (!(priceBlock instanceof HTMLElement)) return;

    const markup = this.#buildJd1QuickViewPriceMarkup(variantId);
    if (!markup.trim()) return;
    priceBlock.innerHTML = markup;
  }

  /**
   * Reads the option-value/media payload generated by snippets/quick-add.liquid.
   * This lets the quick-view modal map swatch clicks to media even when Shopify does not
   * dispatch the variant update fast enough or when the native event only exposes option IDs.
   * @returns {Array<{option_value_id?: string | number, option_name?: string, option_value?: string, option_position?: string | number, fieldset_index?: string | number, input_index?: string | number, variant_id?: string | number | null, media_id?: string | number | null, src?: string | null}>}
   */
  #getJd1QuickViewOptionMediaData() {
    const raw = this.dataset.jd1QvOptions;
    if (!raw) return [];

    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((item) => item && (item.option_value_id || item.variant_id || item.media_id || item.src));
    } catch (error) {
      console.warn('JD1 quick view option/media payload could not be parsed.', error);
      return [];
    }
  }

  /**
   * Normalizes text for loose option/media matching fallbacks.
   * @param {string | null | undefined} value
   * @returns {string}
   */
  #normalizeJd1QvText(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  }

  /**
   * Normalizes Shopify/CDN image URLs before assigning or comparing them.
   * @param {string | null | undefined} src
   * @returns {string}
   */
  #normalizeJd1QuickViewMediaUrl(src) {
    const value = String(src || '').trim();
    if (!value) return '';
    if (value.startsWith('//')) return `${window.location.protocol}${value}`;
    return value;
  }

  /**
   * Creates a stable comparison key for CDN image URLs, ignoring query params and protocol noise.
   * @param {string | null | undefined} src
   * @returns {string}
   */
  #getJd1QuickViewMediaUrlKey(src) {
    const value = this.#normalizeJd1QuickViewMediaUrl(src);
    if (!value) return '';

    try {
      const url = new URL(value, window.location.origin);
      return `${url.hostname}${url.pathname}`.toLowerCase();
    } catch (_error) {
      return value.split('?')[0].replace(/^https?:/, '').toLowerCase();
    }
  }

  /**
   * Finds the option-media payload entry that corresponds to an input/select option in the modal.
   * @param {Element | null | undefined} element
   * @returns {{option_value_id?: string | number, option_name?: string, option_value?: string, option_position?: string | number, fieldset_index?: string | number, input_index?: string | number, variant_id?: string | number | null, media_id?: string | number | null, src?: string | null} | null}
   */
  #getJd1QuickViewOptionMatchForElement(element) {
    if (!element) return null;

    const source = element instanceof HTMLSelectElement ? element.selectedOptions[0] : element;
    if (!(source instanceof HTMLElement)) return null;

    const options = this.#getJd1QuickViewOptionMediaData();
    if (!options.length) return null;

    const dataset = source.dataset || {};
    const optionValueId = dataset.optionValueId;
    const variantId = dataset.variantId;
    const fieldsetIndex = dataset.fieldsetIndex;
    const inputIndex = dataset.inputIndex;
    const value = source instanceof HTMLOptionElement ? source.value : element instanceof HTMLInputElement ? element.value : '';
    const normalizedValue = this.#normalizeJd1QvText(value);

    if (optionValueId) {
      const match = options.find((item) => String(item.option_value_id) === String(optionValueId));
      if (match) return match;
    }

    if (variantId) {
      const match = options.find((item) => String(item.variant_id) === String(variantId));
      if (match) return match;
    }

    if (fieldsetIndex !== undefined && inputIndex !== undefined) {
      const match = options.find(
        (item) => String(item.fieldset_index) === String(fieldsetIndex) && String(item.input_index) === String(inputIndex)
      );
      if (match) return match;
    }

    if (normalizedValue) {
      const match = options.find((item) => this.#normalizeJd1QvText(item.option_value) === normalizedValue);
      if (match) return match;
    }

    return null;
  }

  /**
   * Finds an option-media mapping by option value id.
   * @param {string | number | null | undefined} optionValueId
   * @returns {{option_value_id?: string | number, option_name?: string, option_value?: string, option_position?: string | number, fieldset_index?: string | number, input_index?: string | number, variant_id?: string | number | null, media_id?: string | number | null, src?: string | null} | null}
   */
  #getJd1QuickViewOptionMatchByOptionValueId(optionValueId) {
    if (!optionValueId) return null;
    return (
      this.#getJd1QuickViewOptionMediaData().find((item) => String(item.option_value_id) === String(optionValueId)) || null
    );
  }

  /**
   * Gets the featured media id related to a variant id from the Liquid payload.
   * @param {string | number | null | undefined} variantId
   * @returns {string | null}
   */
  #getJd1QuickViewMediaIdForVariant(variantId) {
    if (!variantId) return null;
    const normalizedVariantId = String(variantId);
    const match = this.#getJd1QuickViewVariantMediaData().find((item) => String(item.id) === normalizedVariantId);
    if (!match?.media_id) return null;
    return String(match.media_id);
  }

  /**
   * Finds the JD1 quick-view modal content when this card owns the opened modal.
   * @returns {Element | null}
   */
  #getActiveJd1QuickViewModalContent() {
    if (!this.hasAttribute('stay-visible')) return null;
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent?.classList.contains('quick-add-modal__content--jd1-qv')) return null;
    return modalContent;
  }

  /**
   * Builds a lightweight, theme-native-looking media rail for optical quick view.
   * This avoids depending on the PDP's current media presentation mode.
   * @returns {HTMLElement | null}
   */
  #buildJd1QuickViewMediaRail() {
    const mediaItems = this.#getJd1QuickViewMediaData();
    if (!mediaItems.length) return null;

    const mediaWrapper = document.createElement('div');
    mediaWrapper.classList.add('jd1-qv-media', 'jd1-qv-visual', 'jd1-qv-media--payload');
    mediaWrapper.setAttribute('data-jd1-qv-visual', 'true');
    mediaWrapper.setAttribute('aria-label', this.dataset.productTitle || 'Product media');

    const viewport = document.createElement('div');
    viewport.classList.add('jd1-qv-visual__viewport');

    const track = document.createElement('div');
    track.classList.add('jd1-qv-visual__track');
    track.setAttribute('data-jd1-qv-visual-track', 'true');

    mediaItems.forEach((item, index) => {
      const src = this.#normalizeJd1QuickViewMediaUrl(item.src || item.src_mobile);
      const mobileSrc = this.#normalizeJd1QuickViewMediaUrl(item.src_mobile || item.src);
      if (!src) return;

      const slide = document.createElement('div');
      slide.classList.add('jd1-qv-visual__slide', 'jd1-qv-payload-slide');
      slide.setAttribute('data-jd1-qv-media-index', String(index));
      slide.setAttribute('data-jd1-qv-media-src', src);
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-label', `${index + 1} / ${mediaItems.length}`);
      if (item.id) slide.setAttribute('data-media-id', String(item.id));

      if (index === 0) {
        slide.classList.add('is-active');
        slide.setAttribute('aria-current', 'true');
      }

      const picture = document.createElement('picture');
      picture.classList.add('jd1-qv-visual__picture');

      if (mobileSrc) {
        const source = document.createElement('source');
        source.media = '(max-width: 749px)';
        source.srcset = mobileSrc;
        picture.appendChild(source);
      }

      const image = document.createElement('img');
      image.classList.add('jd1-qv-visual__image');
      image.src = src;
      image.alt = item.alt || this.dataset.productTitle || '';
      image.loading = index === 0 ? 'eager' : 'lazy';
      image.decoding = 'async';
      image.draggable = false;
      image.sizes = '(max-width: 749px) 82vw, 520px';
      if (index === 0) image.fetchPriority = 'high';

      image.addEventListener('error', () => {
        const fallback = mobileSrc && mobileSrc !== image.src ? mobileSrc : '';
        if (fallback) image.src = fallback;
      }, { once: true });

      picture.appendChild(image);
      slide.appendChild(picture);
      track.appendChild(slide);
    });

    if (!track.children.length) return null;

    viewport.appendChild(track);
    mediaWrapper.appendChild(viewport);

    if (track.children.length > 1) {
      const previousButton = document.createElement('button');
      previousButton.type = 'button';
      previousButton.classList.add('jd1-qv-visual__nav', 'jd1-qv-visual__nav--previous');
      previousButton.setAttribute('aria-label', 'Ver imagen anterior');
      previousButton.setAttribute('data-jd1-qv-nav', 'previous');
      previousButton.innerHTML = '<span aria-hidden="true">‹</span>';

      const nextButton = document.createElement('button');
      nextButton.type = 'button';
      nextButton.classList.add('jd1-qv-visual__nav', 'jd1-qv-visual__nav--next');
      nextButton.setAttribute('aria-label', 'Ver imagen siguiente');
      nextButton.setAttribute('data-jd1-qv-nav', 'next');
      nextButton.innerHTML = '<span aria-hidden="true">›</span>';

      const dots = document.createElement('div');
      dots.classList.add('jd1-qv-visual__dots');
      dots.setAttribute('aria-label', 'Selector de imágenes');

      Array.from(track.children).forEach((_slide, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.classList.add('jd1-qv-visual__dot');
        dot.setAttribute('aria-label', `Ver imagen ${index + 1}`);
        dot.setAttribute('data-jd1-qv-dot-index', String(index));
        if (index === 0) dot.setAttribute('aria-current', 'true');
        dots.appendChild(dot);
      });

      mediaWrapper.appendChild(previousButton);
      mediaWrapper.appendChild(nextButton);
      mediaWrapper.appendChild(dots);
    }

    const zoomButton = document.createElement('button');
    zoomButton.type = 'button';
    zoomButton.classList.add('jd1-qv-zoom-toggle');
    zoomButton.setAttribute('aria-label', 'Activar zoom de producto');
    zoomButton.setAttribute('aria-pressed', 'false');
    zoomButton.innerHTML = '<span aria-hidden="true">+</span><span>Zoom</span>';
    mediaWrapper.appendChild(zoomButton);

    return mediaWrapper;
  }


  /**
   * Activates a specific quick-view media slide.
   * @param {Element} slide
   * @param {Element} modalContent
   * @param {boolean} [shouldScroll]
   */
  #activateJd1QuickViewSlide(slide, modalContent, shouldScroll = true) {
    const media = modalContent.querySelector('.jd1-qv-media');
    if (!media) return;

    const slides = Array.from(
      media.querySelectorAll('.jd1-qv-visual__slide, .jd1-qv-payload-slide, .product-media-container[data-media-id]')
    );
    const activeIndex = slides.indexOf(slide);

    slides.forEach((item, index) => {
      const isActive = item === slide;
      item.classList.toggle('is-active', isActive);
      if (isActive) {
        item.setAttribute('aria-current', 'true');
      } else {
        item.removeAttribute('aria-current');
      }

      const dot = media.querySelector(`[data-jd1-qv-dot-index="${index}"]`);
      if (dot) {
        if (isActive) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      }
    });

    const track = media.querySelector('.jd1-qv-visual__track');
    if (track instanceof HTMLElement && activeIndex >= 0) {
      track.style.transform = `translate3d(${-activeIndex * 100}%, 0, 0)`;
    }

    this.#resetJd1QuickViewZoom(modalContent);

    if (!shouldScroll || track) return;

    const scroller = slide.closest('.media-gallery__grid, slideshow-slides');
    if (scroller instanceof HTMLElement) {
      scroller.scrollTo({ left: slide.offsetLeft, top: 0, behavior: 'smooth' });
    }
  }


  /**
   * Selects the modal image that matches a media id.
   * @param {string | number | null | undefined} mediaId
   * @param {Element} modalContent
   * @returns {boolean}
   */
  #selectJd1QuickViewMediaById(mediaId, modalContent) {
    if (!mediaId) return false;
    const media = modalContent.querySelector('.jd1-qv-media');
    if (!media) return false;

    const normalizedMediaId = String(mediaId);
    const slides = Array.from(media.querySelectorAll('.jd1-qv-visual__slide, .jd1-qv-payload-slide, .product-media-container[data-media-id]'));
    const slide = slides.find((item) => String(item.getAttribute('data-media-id')) === normalizedMediaId);

    if (!slide) return false;

    this.#activateJd1QuickViewSlide(slide, modalContent, true);
    return true;
  }

  /**
   * Selects the modal image that matches an image src generated in the Liquid payload.
   * @param {string | null | undefined} src
   * @param {Element} modalContent
   * @returns {boolean}
   */
  #selectJd1QuickViewMediaBySrc(src, modalContent) {
    if (!src) return false;
    const media = modalContent.querySelector('.jd1-qv-media');
    if (!media) return false;

    const normalizedSrc = this.#getJd1QuickViewMediaUrlKey(src);
    const slides = Array.from(media.querySelectorAll('.jd1-qv-visual__slide, .jd1-qv-payload-slide, .product-media-container'));
    const slide = slides.find((item) => {
      const attrSrc = item.getAttribute('data-jd1-qv-media-src');
      const image = item.querySelector('img');
      const imageSrc = image instanceof HTMLImageElement ? image.currentSrc || image.src || image.getAttribute('src') : '';
      return (attrSrc && this.#getJd1QuickViewMediaUrlKey(attrSrc) === normalizedSrc) || (imageSrc && this.#getJd1QuickViewMediaUrlKey(imageSrc) === normalizedSrc);
    });

    if (!slide) return false;

    this.#activateJd1QuickViewSlide(slide, modalContent, true);
    return true;
  }

  /**
   * Selects a modal image by index as a final fallback. Optical catalogues usually keep media order
   * aligned with swatch order, so this is safer than doing nothing when variants lack featured_media.
   * @param {string | number | null | undefined} index
   * @param {Element} modalContent
   * @returns {boolean}
   */
  #selectJd1QuickViewMediaByIndex(index, modalContent) {
    if (index === null || index === undefined || index === '') return false;
    const media = modalContent.querySelector('.jd1-qv-media');
    if (!media) return false;

    const numericIndex = Number(index);
    if (!Number.isInteger(numericIndex) || numericIndex < 0) return false;

    const slides = Array.from(media.querySelectorAll('.jd1-qv-visual__slide, .jd1-qv-payload-slide, .product-media-container'));
    const slide = slides[numericIndex];
    if (!slide) return false;

    this.#activateJd1QuickViewSlide(slide, modalContent, true);
    return true;
  }

  /**
   * Selects modal media using the richest available option/variant mapping.
   * @param {{option_value_id?: string | number, option_name?: string, option_value?: string, option_position?: string | number, fieldset_index?: string | number, input_index?: string | number, variant_id?: string | number | null, media_id?: string | number | null, src?: string | null} | null | undefined} match
   * @param {Element} modalContent
   * @returns {boolean}
   */
  #selectJd1QuickViewMediaForOptionMatch(match, modalContent) {
    if (!match) return false;

    if (match.media_id && this.#selectJd1QuickViewMediaById(match.media_id, modalContent)) return true;
    if (match.variant_id && this.#selectJd1QuickViewMediaForVariant(match.variant_id, modalContent)) return true;
    if (match.src && this.#selectJd1QuickViewMediaBySrc(match.src, modalContent)) return true;
    if (this.#selectJd1QuickViewMediaByIndex(match.input_index, modalContent)) return true;

    return false;
  }

  /**
   * Selects the modal image that matches a variant id.
   * @param {string | number | null | undefined} variantId
   * @param {Element} modalContent
   * @returns {boolean}
   */
  #selectJd1QuickViewMediaForVariant(variantId, modalContent) {
    if (!variantId) return false;

    const mediaId = this.#getJd1QuickViewMediaIdForVariant(variantId);
    if (mediaId && this.#selectJd1QuickViewMediaById(mediaId, modalContent)) return true;

    const variantMatch = this.#getJd1QuickViewOptionMediaData().find(
      (item) => item.variant_id && String(item.variant_id) === String(variantId)
    );

    if (variantMatch) {
      if (variantMatch.media_id && this.#selectJd1QuickViewMediaById(variantMatch.media_id, modalContent)) return true;
      if (variantMatch.src && this.#selectJd1QuickViewMediaBySrc(variantMatch.src, modalContent)) return true;
      if (this.#selectJd1QuickViewMediaByIndex(variantMatch.input_index, modalContent)) return true;
    }

    return false;
  }

  /**
   * Syncs the active media slide with the selected variant inside the modal.
   * @param {Element} modalContent
   */
  #syncJd1QuickViewMediaFromModalSelection(modalContent) {
    const checkedInput = modalContent.querySelector(
      'variant-picker input[type="radio"]:checked, variant-picker select option:checked'
    );
    const match = checkedInput instanceof Element ? this.#getJd1QuickViewOptionMatchForElement(checkedInput) : null;

    if (this.#selectJd1QuickViewMediaForOptionMatch(match, modalContent)) return;

    const variantId = checkedInput instanceof HTMLElement ? checkedInput.dataset.variantId : null;
    if (variantId) this.#selectJd1QuickViewMediaForVariant(variantId, modalContent);
  }

  /**
   * Clears the zoom state when the selected media changes.
   * @param {Element} modalContent
   */
  #resetJd1QuickViewZoom(modalContent) {
    const media = modalContent.querySelector('.jd1-qv-media');
    const zoomButton = modalContent.querySelector('.jd1-qv-zoom-toggle');

    media?.classList.remove('is-zoomed');

    if (zoomButton instanceof HTMLButtonElement) {
      zoomButton.setAttribute('aria-pressed', 'false');
      zoomButton.setAttribute('aria-label', 'Activar zoom de producto');
    }
  }

  /**
   * Toggles product media zoom inside the quick-view modal.
   * @param {Element} modalContent
   */
  #toggleJd1QuickViewZoom(modalContent) {
    const media = modalContent.querySelector('.jd1-qv-media');
    const zoomButton = modalContent.querySelector('.jd1-qv-zoom-toggle');
    if (!media) return;

    const isZoomed = !media.classList.contains('is-zoomed');
    media.classList.toggle('is-zoomed', isZoomed);

    if (zoomButton instanceof HTMLButtonElement) {
      zoomButton.setAttribute('aria-pressed', String(isZoomed));
      zoomButton.setAttribute('aria-label', isZoomed ? 'Desactivar zoom de producto' : 'Activar zoom de producto');
    }
  }

  /**
   * Keeps the active slide state synced when the user drags the horizontal media rail.
   * @param {HTMLElement} scroller
   * @param {Element} modalContent
   */
  #updateActiveJd1QuickViewSlideFromScroll(scroller, modalContent) {
    const slides = Array.from(scroller.querySelectorAll('.jd1-qv-payload-slide, .product-media-container[data-media-id]'));
    if (!slides.length) return;

    const scrollerCenter = scroller.scrollLeft + scroller.clientWidth / 2;
    let closestSlide = slides[0];
    let closestDistance = Number.POSITIVE_INFINITY;

    slides.forEach((slide) => {
      if (!(slide instanceof HTMLElement)) return;
      const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
      const distance = Math.abs(scrollerCenter - slideCenter);
      if (distance < closestDistance) {
        closestSlide = slide;
        closestDistance = distance;
      }
    });

    this.#activateJd1QuickViewSlide(closestSlide, modalContent, false);
  }

  /**
   * Adds JD1 media helper data to the native variant inputs after the PDP fragment is morphed.
   * @param {Element} modalContent
   */
  #annotateJd1QuickViewVariantInputs(modalContent) {
    const optionElements = modalContent.querySelectorAll('variant-picker input[data-option-value-id], variant-picker option[data-option-value-id]');

    optionElements.forEach((element) => {
      if (!(element instanceof HTMLElement)) return;
      const match = this.#getJd1QuickViewOptionMatchForElement(element);
      if (!match) return;

      if (match.media_id) element.dataset.jd1QvMediaId = String(match.media_id);
      if (match.variant_id) element.dataset.jd1QvVariantId = String(match.variant_id);
      if (match.src) element.dataset.jd1QvMediaSrc = String(match.src);
      if (match.input_index !== undefined) element.dataset.jd1QvMediaIndex = String(match.input_index);
    });
  }

  /**
   * Moves the custom JD1 visual carousel by one slide.
   * @param {'previous' | 'next'} direction
   * @param {Element} modalContent
   */
  #moveJd1QuickViewMedia(direction, modalContent) {
    const media = modalContent.querySelector('.jd1-qv-media');
    if (!media) return;

    const slides = Array.from(media.querySelectorAll('.jd1-qv-visual__slide, .jd1-qv-payload-slide, .product-media-container'));
    if (!slides.length) return;

    const currentIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    const nextIndex = direction === 'previous'
      ? (currentIndex - 1 + slides.length) % slides.length
      : (currentIndex + 1) % slides.length;

    this.#activateJd1QuickViewSlide(slides[nextIndex], modalContent, true);
  }

  /**
   * Binds quick-view media interactions: swatch-driven image changes and zoom.
   * @param {Element} modalContent
   */
  #initJd1QuickViewMediaControls(modalContent) {
    this.#quickViewModalAbortController?.abort();
    this.#quickViewModalAbortController = new AbortController();
    const { signal } = this.#quickViewModalAbortController;

    modalContent.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;
        if (!target.closest('variant-picker')) return;

        const selectedSource = target instanceof HTMLSelectElement ? target.selectedOptions[0] : target;
        const explicitMediaId = selectedSource instanceof HTMLElement ? selectedSource.dataset.jd1QvMediaId : null;
        const explicitMediaSrc = selectedSource instanceof HTMLElement ? selectedSource.dataset.jd1QvMediaSrc : null;
        const explicitMediaIndex = selectedSource instanceof HTMLElement ? selectedSource.dataset.jd1QvMediaIndex : null;
        const selectedVariantId = target instanceof HTMLInputElement || target instanceof HTMLSelectElement
          ? target.dataset.variantId || (target instanceof HTMLSelectElement ? target.selectedOptions[0]?.dataset.variantId : null)
          : null;

        if (selectedVariantId) this.#updateJd1QuickViewPrice(selectedVariantId, modalContent);

        if (explicitMediaId && this.#selectJd1QuickViewMediaById(explicitMediaId, modalContent)) return;
        if (explicitMediaSrc && this.#selectJd1QuickViewMediaBySrc(explicitMediaSrc, modalContent)) return;
        if (explicitMediaIndex && this.#selectJd1QuickViewMediaByIndex(explicitMediaIndex, modalContent)) return;

        const match = this.#getJd1QuickViewOptionMatchForElement(target);
        if (match?.variant_id) this.#updateJd1QuickViewPrice(match.variant_id, modalContent);
        if (this.#selectJd1QuickViewMediaForOptionMatch(match, modalContent)) return;

        if (selectedVariantId) this.#selectJd1QuickViewMediaForVariant(selectedVariantId, modalContent);
      },
      { signal }
    );

    modalContent.addEventListener(
      ThemeEvents.variantSelected,
      (event) => {
        const optionValueId = event.detail?.resource?.id;
        const match = this.#getJd1QuickViewOptionMatchByOptionValueId(optionValueId);
        if (match?.variant_id) this.#updateJd1QuickViewPrice(match.variant_id, modalContent);
        this.#selectJd1QuickViewMediaForOptionMatch(match, modalContent);
      },
      { signal }
    );

    modalContent.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const navButton = target.closest('[data-jd1-qv-nav]');
        if (navButton instanceof HTMLElement) {
          event.preventDefault();
          const direction = navButton.dataset.jd1QvNav === 'previous' ? 'previous' : 'next';
          this.#moveJd1QuickViewMedia(direction, modalContent);
          return;
        }

        const dotButton = target.closest('[data-jd1-qv-dot-index]');
        if (dotButton instanceof HTMLElement) {
          event.preventDefault();
          this.#selectJd1QuickViewMediaByIndex(dotButton.dataset.jd1QvDotIndex, modalContent);
          return;
        }

        if (target.closest('.jd1-qv-zoom-toggle')) {
          event.preventDefault();
          this.#toggleJd1QuickViewZoom(modalContent);
          return;
        }

        if (target.closest('.jd1-qv-media .jd1-qv-visual__image, .jd1-qv-media .product-media__image, .jd1-qv-media picture')) {
          this.#toggleJd1QuickViewZoom(modalContent);
        }
      },
      { signal }
    );

    const scroller = modalContent.querySelector('.jd1-qv-media .media-gallery__grid, .jd1-qv-media slideshow-slides');
    if (scroller instanceof HTMLElement) {
      let scrollRaf = 0;
      scroller.addEventListener(
        'scroll',
        () => {
          if (scrollRaf) cancelAnimationFrame(scrollRaf);
          scrollRaf = requestAnimationFrame(() => this.#updateActiveJd1QuickViewSlideFromScroll(scroller, modalContent));
        },
        { passive: true, signal }
      );
    }
  }

  /**
   * Updates the quick-view image when the native variant picker finishes updating.
   * @param {CustomEvent} event
   */
  #handleJd1QvVariantUpdate = (event) => {
    const modalContent = this.#getActiveJd1QuickViewModalContent();
    if (!modalContent) return;

    this.#annotateJd1QuickViewVariantInputs(modalContent);

    const resource = event.detail?.resource;
    const variantId = resource?.id;
    if (variantId) this.#updateJd1QuickViewPrice(variantId, modalContent);

    const mediaId = resource?.featured_media?.id || this.#getJd1QuickViewMediaIdForVariant(variantId);

    if (mediaId && this.#selectJd1QuickViewMediaById(mediaId, modalContent)) return;

    const optionValueId = event.detail?.sourceId;
    const match = this.#getJd1QuickViewOptionMatchByOptionValueId(optionValueId);
    if (match?.variant_id) this.#updateJd1QuickViewPrice(match.variant_id, modalContent);
    this.#selectJd1QuickViewMediaForOptionMatch(match, modalContent);
  };

  /**
   * Builds the JD1 optical quick-view modal layout from the native product grid.
   * The fetched PDP remains the source of truth; we only rearrange native media,
   * variant picker and product form into a consultative eyewear preview.
   * @param {Element} productGrid - The product grid element fetched from the PDP
   */
  #prepareJd1OpticalQuickView(productGrid) {
    productGrid.classList.add('quick-add-modal__content--jd1-qv');
    productGrid.setAttribute('data-jd1-qv-layout', 'optical-carousel');

    const nativeMedia = productGrid.querySelector('.product-information__media');
    const payloadMedia = this.#buildJd1QuickViewMediaRail();
    const media = payloadMedia || nativeMedia;
    const productDetails = productGrid.querySelector('.product-details');
    const variantPicker = productGrid.querySelector('variant-picker');
    const productPrice = productGrid.querySelector('product-price');
    const buyButtonsBlock = productGrid.querySelector('.buy-buttons-block');
    // JD1 · CP-QV02 V1
    // If the PDP uses the custom CTA + favorite block, move its full root into
    // quick view. Moving only product-form-component strips the parent scope that
    // keeps the favorite button compact.
    const jd1CtaFavBlock = productGrid.querySelector('.jd1-pdp-ctafav');
    const productFormComponent = productGrid.querySelector('product-form-component');

    let titleBlock = productGrid.querySelector('.view-product-title');
    let titleLink = titleBlock?.querySelector('a');

    if (!titleBlock) {
      titleBlock = document.createElement('div');
      titleBlock.classList.add('view-product-title');
    }

    if (!titleLink) {
      titleLink = document.createElement('a');
      titleLink.classList.add('link');
      titleBlock.appendChild(titleLink);
    }

    titleLink.textContent = this.dataset.productTitle || titleLink.textContent || '';
    titleLink.href = this.productPageUrl;

    const shell = document.createElement('div');
    shell.classList.add('jd1-qv-shell');
    if (this.dataset.jd1QvVariants) shell.dataset.jd1QvVariants = this.dataset.jd1QvVariants;

    const header = document.createElement('div');
    header.classList.add('jd1-qv-header');
    titleBlock.classList.add('jd1-qv-title');
    header.appendChild(titleBlock);

    const mediaRegion = document.createElement('div');
    mediaRegion.classList.add('jd1-qv-media-region');
    if (media) {
      media.classList.add('jd1-qv-media');
      mediaRegion.appendChild(media);
    } else {
      mediaRegion.classList.add('jd1-qv-media-region--empty');
    }

    const details = document.createElement('div');
    details.classList.add('jd1-qv-details');

    const controls = document.createElement('div');
    controls.classList.add('jd1-qv-controls');

    if (variantPicker) {
      variantPicker.classList.add('jd1-qv-variant-picker');
      controls.appendChild(variantPicker);
    }

    // JD1 · CP-QV01 V10
    // Price is rendered from the Liquid variant payload so it always appears below
    // the swatches and updates when the quick-view variant changes.
    productPrice?.remove();
    const jd1PriceBlock = this.#createJd1QuickViewPriceBlock(this.#getSelectedVariantId());
    if (jd1PriceBlock) {
      controls.appendChild(jd1PriceBlock);
    }

    const cta = document.createElement('div');
    cta.classList.add('jd1-qv-cta');

    if (buyButtonsBlock) {
      cta.appendChild(buyButtonsBlock);
    } else if (jd1CtaFavBlock) {
      jd1CtaFavBlock.classList.add('jd1-qv-ctafav');
      cta.appendChild(jd1CtaFavBlock);
    } else if (productFormComponent) {
      productFormComponent.classList.add('jd1-qv-product-form-fallback');
      cta.appendChild(productFormComponent);
    }

    controls.appendChild(cta);

    shell.appendChild(mediaRegion);
    details.appendChild(header);
    details.appendChild(controls);
    shell.appendChild(details);

    productGrid.replaceChildren(shell);
    productDetails?.remove();
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    const dialog = document.querySelector('#quick-add-dialog .quick-add-modal');
    dialog?.classList.add('quick-add-modal--jd1-qv');
    modalContent.classList.add('quick-add-modal__content--jd1-qv');
    modalContent.setAttribute('data-jd1-qv-layout', 'optical-carousel');

    this.#prepareJd1OpticalQuickView(productGrid);

    morph(modalContent, productGrid);

    this.#annotateJd1QuickViewVariantInputs(modalContent);
    this.#initJd1QuickViewMediaControls(modalContent);
    this.#syncVariantSelection(modalContent);
    this.#annotateJd1QuickViewVariantInputs(modalContent);
    this.#syncJd1QuickViewMediaFromModalSelection(modalContent);
    this.#updateJd1QuickViewPrice(this.#getSelectedVariantId(), modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;

    // JD1 · CP-QV01 V1
    // Optical/consultative retail cards can force the CTA to remain a quick-view trigger.
    // This prevents a selected swatch from switching the card button back to direct add-to-cart.
    if (this.dataset.forceModal === 'true') {
      this.setAttribute('data-quick-add-button', 'choose');
      return;
    }

    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
    }

    this.#selectJd1QuickViewMediaForVariant(selectedVariantId, modalContent);
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (
      this.querySelector('.product-header a, .jd1-qv-title a')
    );

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}